import React, { createContext, useEffect, useRef } from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { ensureApiJwtTokenRefreshed } from '../../../../context/AuthContext';
import { WebSocketMessage } from './';

const getWebsocketBaseUrl = () => {
  if (import.meta.env.VITE_APP_WEBSOCKET_URL) {
    return import.meta.env.VITE_APP_WEBSOCKET_URL;
  }
  return `wss://${window.location.host}/ws`;
};

// Send frame to websocket to keep AWS connection alive.
// By default AWS drop connection after 10 minutes of silence
const HEARTBEAT_INTERVAL = 9 * 60 * 1000;

export const WebSocketContext = createContext<{}>({});

type Props = {
  socketStateChanged: (opened: boolean, openTryNumber: number) => void;
  messageReceived: (message: WebSocketMessage) => void;
};

export const WebSocketProvider: React.FC<React.PropsWithChildren<Props>> = ({
  messageReceived,
  socketStateChanged,
  ...rest
}) => {
  const ws = useRef<ReconnectingWebSocket | null>(null);
  const wsOpenAttempts = useRef<number>(0);
  const heartBeatIntervalId = useRef<NodeJS.Timeout | null>(null);

  const apiToken = useApiTokenRefreshed();

  useEffect(() => {
    if (!apiToken) {
      return;
    }

    const heartBeat = () => {
      const webSocket = ws.current;
      if (!webSocket) {
        return;
      }
      if (webSocket.readyState !== webSocket.OPEN) {
        return;
      }
      webSocket.send(JSON.stringify({ message: 'ping' }));
    };

    const baseUrl = getWebsocketBaseUrl();
    const url = `${baseUrl}?authorization=${encodeURIComponent(apiToken)}`;
    ws.current = new ReconnectingWebSocket(url);
    ws.current.onopen = () => {
      wsOpenAttempts.current = wsOpenAttempts.current + 1;
      socketStateChanged(true, wsOpenAttempts.current);
      heartBeatIntervalId.current = setInterval(heartBeat, HEARTBEAT_INTERVAL);
    };
    ws.current.onclose = () => {
      if (heartBeatIntervalId.current) {
        clearInterval(heartBeatIntervalId.current);
      }
      socketStateChanged(false, wsOpenAttempts.current);
    };
    ws.current.onmessage = (m) => {
      const message = JSON.parse(m.data) as WebSocketMessage;
      messageReceived(message);
    };

    const wsCurrent = ws.current;

    return () => {
      wsCurrent.close();
    };
  }, [ws, apiToken, messageReceived, socketStateChanged]);

  return <WebSocketContext.Provider value={{}} {...rest} />;
};

// Return refreshed token only in first call, later do not update it (but I think it is not required)
// Try to not use that hook, prefer explicit call to ensureApiJwtTokenRefreshed
// null: we don't have apiToken
// undefined: we don't know yet
const useApiTokenRefreshed = (): string | null | undefined => {
  const [apiToken, setApiToken] = React.useState<string | null | undefined>(
    undefined,
  );
  React.useEffect(() => {
    if (apiToken === undefined) {
      ensureApiJwtTokenRefreshed().then((newToken) => {
        setApiToken(newToken);
      });
    }
  }, [apiToken, setApiToken]);

  return apiToken;
};
